import React, { useEffect } from 'react';
import Button from 'react-bootstrap/Button';
import BodyMatcher from 'components/transactionEditor/BodyMatcher';
import { getUniqueIndex } from 'components/transactionEditor/RequestMatchersList';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import BzmIcon from '../icons/BzmIcon';
import _ from 'lodash';
import { getMatcherType } from '../../utils/miscUtil';
import { resolveMatcherSampleBody } from '../../utils/transactionUtils';
import { Matcher } from '../../types/rest/dslTypes';
import { RequestNodeWidgetType } from './RequestNodeWidget';
import { showToastNotification } from '../../store/ui/toasts/toastsActions';
import { ToastType } from '../../store/ui/toasts/toastsTypes';

export interface BodyMatcherItem {
  id: string;
  matcher: Matcher | null;
  content: string | null;
  cdataXpath?: string | null;
  sampleBody?: string | null;
  /** Computed once per render by BodyMatchersList: this row's own sampleBody if set, otherwise the
   *  legacy fallback (same value getSampleBody() returns). Lets RequestNodeWidget's wizard scan for
   *  sibling rows that share this row's sample body without needing legacySampleBody threaded down
   *  separately. Never itself persisted — recomputed on every render. */
  resolvedSampleBody?: string | null;
  namespaces: XmlNamespaceItem[];
  isRequired?: boolean;
  [key: string]: string | boolean | XmlNamespaceItem[] | null;
}

export interface XmlNamespaceItem {
  id: string;
  prefix: string | null;
  uri: string | null;
  [key: string]: string | null;
}

export interface BodyMatcherProps {
  id: string;
  bodyMatcherList: BodyMatcherItem[];
  /** Raw, unparsed legacy transaction-level sample body — read-only, used only as a
   *  fallback default for rows that don't have their own sampleBody yet. */
  legacySampleBody?: string | null;
  onListChange: (list: BodyMatcherItem[]) => void;
  onXmlNamespaceListChange: (list: XmlNamespaceItem[]) => void;
  isReadOnly?: boolean;
  isWizardModal?: boolean;
  showByDefaultOneRow?: boolean;
  enableAiAssistant?: boolean; // Flag to enable AI assistant functionality
  transactionId?: number;
}

export interface MatcherPathItem {
  value: string;
  id: string;
  cdataXpath?: string;
}

const BodyMatchersList = (props: BodyMatcherProps) => {
  const { t } = useTranslation('transactions');
  const dispatch = useDispatch();

  useEffect(() => {
    // Code to run on component mount
    if (props.showByDefaultOneRow === true) {
      props.bodyMatcherList.length === 0 && handleAddNew();
    }
  }, []);

  const handleChange = async (name: string, value: string, id: string) => {
    if (props.bodyMatcherList) {
      const list = _.cloneDeep(props.bodyMatcherList);
      const index: number = list.findIndex(item => item.id === id);
      list[index][name] = value;

      // If we are changing a matcher type, reset the required field to default value
      // (it is visible only in certain scenarios, and user might not be able to
      // change it in the current matcher)
      if (name === 'matcher') {
        list[index].isRequired = true;
      }

      await props.onListChange(list);
    }
  };

  /**
   * Updates the list of body matchers with new matchers generated from wizard widget.
   * @param matchers - List of matchers to be added
   * @param type - Type of the request node widget (e.g., 'xpath', 'cdata_xpath', etc.)
   * @param namespaces - Optional list of XML namespaces to be associated with the matchers
   * @param sourceId - id of the row whose wizard invoked this change (see RequestNodeWidget's
   *   selectNodes, which ids generated matchers `sourceId` and `sourceId + '-' + index`)
   * @param sampleBody - the sample body shown in the wizard session that produced `matchers`;
   *   carried over onto the resulting row(s) so re-opening the wizard doesn't show it as empty.
   */
  const handleChangeMultipleValues = (
    matchers: MatcherPathItem[],
    type: RequestNodeWidgetType,
    namespaces?: XmlNamespaceItem[],
    sourceId?: string,
    sampleBody?: string,
  ) => {
    if (props.bodyMatcherList) {
      let bodyMatcherItems = _.cloneDeep(props.bodyMatcherList);
      let matcherType: Matcher;
      switch (type) {
        case 'xpath':
          matcherType = Matcher.matches_xml;
          break;
        case 'cdata_xpath':
          matcherType = Matcher.matches_xml_cdata;
          break;
        default:
          matcherType = Matcher.matches_json;
          break;
      }

      if (type === 'cdata_xpath') {
        // CData sections are edited as one shared session across every matches_xml_cdata
        // row in the transaction (the CData dropdown in RequestNodeWidget switches
        // between sections within a single wizard instance), so replacing every row of
        // this type is correct here.
        bodyMatcherItems = _.filter(bodyMatcherItems, matcherItem => matcherItem.matcher !== matcherType);
      } else {
        // json/xpath: replace the rows that belong to the same wizard "family" as the row that
        // invoked it (the row itself, plus any sibling rows it previously generated), AND any
        // other row whose saved matcher was generated against this exact sample body — even one
        // outside that id-based family, e.g. a sibling row the wizard was reopened from directly
        // rather than from the family's original root. This mirrors the scope
        // RequestNodeWidget's checkbox preselection scan uses (see initializeLeafNodeValues): if
        // reopening the wizard can show a row's node pre-checked, deselecting it here must be
        // able to remove that same row, instead of leaving a stale duplicate behind (which the
        // duplicate check above would then silently swallow, turning this row into a phantom
        // empty placeholder rather than actually removing it). Not every row of the same matcher
        // type, though — that would wipe out unrelated rows edited independently.
        bodyMatcherItems = _.filter(
          bodyMatcherItems,
          matcherItem =>
            matcherItem.id !== sourceId &&
            !matcherItem.id.startsWith(sourceId + '-') &&
            !(sampleBody && getSampleBody(matcherItem) === sampleBody),
        );
      }

      let duplicateCount = 0;
      if (matchers.length > 0) {
        matchers.forEach(m => {
          // A matcher identical in type + content might already exist on an unrelated row (e.g.
          // the user re-running the wizard against the same sample content from a different, new
          // row). Scan the whole list — not just this row's family — and silently skip creating
          // an exact duplicate, mirroring the pre-rework behavior where every row of a type shared
          // one sample body slot and could never grow duplicate matchers this way. cdata_xpath is
          // excluded: that branch already replaced every row of the type above, so there's nothing
          // left to duplicate against.
          const isDuplicate =
            type !== 'cdata_xpath' &&
            _.some(bodyMatcherItems, existing => existing.matcher === matcherType && existing.content === m.value);
          if (isDuplicate) {
            duplicateCount++;
            return;
          }
          const newMatcher: BodyMatcherItem = {
            id: m.id,
            matcher: matcherType,
            content: m.value,
            cdataXpath: m.cdataXpath,
            namespaces: namespaces,
            sampleBody: sampleBody ?? null,
          };
          bodyMatcherItems.push(newMatcher);
        });
      }
      // If matchers is empty (every node deselected), nothing is pushed back for this row's
      // family — it was already cleared above, so the row(s) simply disappear. This matches
      // pre-rework behavior (a single shared sample-body slot per matcher type never left an
      // empty placeholder behind either) and keeps full deselection consistent with partial
      // deselection, where an unchecked sibling row is already removed rather than blanked.

      if (
        duplicateCount > 0 &&
        type !== 'cdata_xpath' &&
        sourceId &&
        !_.some(
          bodyMatcherItems,
          matcherItem => matcherItem.id === sourceId || matcherItem.id.startsWith(sourceId + '-'),
        )
      ) {
        // Every matcher generated by this wizard invocation turned out to be a duplicate of one
        // already on an unrelated row, so the forEach above pushed nothing back for this row's
        // whole family (sourceId or any sourceId + '-' sibling) — without this, the row that
        // invoked the wizard would silently vanish from the list. Push the same empty placeholder
        // as the deselect-everything branch above so the row survives. Checking the full family,
        // not just the exact sourceId, matters for a mixed batch: if the first-generated matcher
        // (which always carries id === sourceId) is a duplicate but a later sibling in the same
        // batch (sourceId + '-1', etc.) is genuinely new, that sibling already keeps this row's
        // family represented — pushing a placeholder here too would add a spurious empty row.
        bodyMatcherItems.push({
          id: sourceId,
          matcher: matcherType,
          content: null,
          namespaces: namespaces,
          sampleBody: sampleBody ?? null,
        });
      }

      if (duplicateCount > 0) {
        dispatch(
          showToastNotification(
            ToastType.Warning,
            t('transactions:editor.duplicateBodyMatchersSkippedTitle'),
            t('transactions:editor.duplicateBodyMatchersSkippedMessage', { count: duplicateCount }),
          ),
        );
      }

      props.onListChange(bodyMatcherItems);
    }
  };

  /**
   * Persists the wizard's sample body text onto the row(s) it belongs to, independent of the
   * matchers those rows carry. Used when the wizard closes without an explicit "Select" (Cancel,
   * backdrop click, Esc, the header close button) so typed/built sample body isn't lost on close,
   * while leaving any already-saved matcher content untouched. Scoped narrowly to sourceId's own
   * id-based family (not the broader resolvedSampleBody match handleChangeMultipleValues also
   * considers) — widening this would overwrite an unrelated row's sampleBody with content that
   * no longer matches that row's own saved matcher.
   * @param type - Type of the request node widget (e.g., 'xpath', 'cdata_xpath', etc.)
   * @param sourceId - id of the row whose wizard this sample body was shown in
   * @param sampleBody - the sample body currently shown in the wizard
   */
  const handleSampleBodyChange = (type: RequestNodeWidgetType, sourceId: string, sampleBody: string) => {
    if (!props.bodyMatcherList) {
      return;
    }
    const bodyMatcherItems = _.cloneDeep(props.bodyMatcherList);
    const nextSampleBody = sampleBody ?? null;
    let changed = false;
    bodyMatcherItems.forEach(item => {
      const belongsToFamily =
        type === 'cdata_xpath'
          ? item.matcher === Matcher.matches_xml_cdata
          : item.id === sourceId || item.id.startsWith(sourceId + '-');
      // Only write (and mark the list changed) when the value actually differs — a redundant
      // call, or a sibling that already holds this exact sample body, must not trigger an
      // unnecessary onListChange/re-render.
      if (belongsToFamily && (item.sampleBody ?? null) !== nextSampleBody) {
        item.sampleBody = nextSampleBody;
        changed = true;
      }
    });
    if (changed) {
      props.onListChange(bodyMatcherItems);
    }
  };

  const handleAddNew = () => {
    const emptyItem = {
      id: 'matcher-item-' + getUniqueIndex(props.bodyMatcherList),
      matcher: Matcher.equals,
    } as BodyMatcherItem;
    const list = props.bodyMatcherList ? props.bodyMatcherList.concat(emptyItem) : [emptyItem];
    props.onListChange(list);
  };

  const handleDelete = (index: number) => {
    if (props.showByDefaultOneRow === true && props.bodyMatcherList.length === 1) {
      return;
    }
    const items = _.cloneDeep(props.bodyMatcherList);
    items.splice(index, 1);
    props.onListChange(items);
  };

  /**
   * Gets this matcher row's own sample body if set; otherwise falls back to the legacy,
   * read-only transaction-level sample body (parsed once, never written back).
   * @param bodyMatcher
   */
  const getSampleBody = (bodyMatcher: BodyMatcherItem) => {
    return resolveMatcherSampleBody(
      bodyMatcher.sampleBody,
      props.legacySampleBody,
      getMatcherType(bodyMatcher.matcher),
    );
  };

  if (props.bodyMatcherList && props.bodyMatcherList.length > 0) {
    // Attach each row's resolved sample body (its own value, or the legacy fallback) so
    // RequestNodeWidget can scan for sibling rows that share the same sample body when
    // preselecting wizard checkboxes — see initializeLeafNodeValues in RequestNodeWidget.
    const bodyMatcherListWithResolvedSampleBody: BodyMatcherItem[] = props.bodyMatcherList.map(item => ({
      ...item,
      resolvedSampleBody: getSampleBody(item),
    }));
    const length = bodyMatcherListWithResolvedSampleBody.length;
    return (
      <div id="qa-body-matchers">
        <p>{t('transactions:editor.nonEmptyListMessage')}</p>
        {bodyMatcherListWithResolvedSampleBody.map((item: BodyMatcherItem, index: number) => (
          <div key={'matcher-' + index} className={'mb-5'}>
            <BodyMatcher
              isReadOnly={props.isReadOnly}
              sampleBody={item.resolvedSampleBody}
              onChange={handleChange}
              onChangeMultipleValues={handleChangeMultipleValues}
              onChangeSampleBody={handleSampleBodyChange}
              onXmlNamespaceChange={props.onXmlNamespaceListChange}
              onAdd={handleAddNew}
              onDelete={() => handleDelete(index)}
              displayAdd={index === length - 1}
              key={item.id}
              id={item.id}
              matcherItem={item}
              isWizardModal={props.isWizardModal}
              bodyMatcherList={bodyMatcherListWithResolvedSampleBody}
              enableAiAssistant={props.enableAiAssistant}
              transactionId={props.transactionId}
            />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div id={props.id}>
      <p>{t('editor.emptyBodyMatcherListMessage')}</p>
      <Button
        disabled={props.isReadOnly}
        id="qa-btn-create-first-body-matcher"
        onClick={handleAddNew}
        variant="primary"
        className="btn-circular"
        title={t('editor.addNewBodyMatcher')}
      >
        <BzmIcon variant={'icon-bzm-add'} />
      </Button>
    </div>
  );
};

export default BodyMatchersList;
